import dynamic from 'next/dynamic';
import { type FC, type JSX, type ReactNode } from 'react';

import { type ClientAuthenticatedUserWithSelectedRole } from '@/src/auth';
import { type PageAuth } from '@/src/components-v2/types';
import { PageAuthType } from '@/src/middleware/pages';

import { BootstrappedFeatureFlagProvider } from './BootstrappedFeatureFlagProvider';
import * as util from './util';

const AsyncFeatureFlagProvider = dynamic(
  () => import('./AsyncFeatureFlagProvider').then(mod => mod.AsyncFeatureFlagProvider),
  { ssr: false },
);

interface FeatureFlagProviderProps {
  readonly auth: PageAuth<PageAuthType.Auth0> | PageAuth<PageAuthType.Lti>;
  readonly children: ReactNode;
  readonly user: ClientAuthenticatedUserWithSelectedRole;
}

const FeatureFlagProviderInner: FC<FeatureFlagProviderProps> = ({
  auth,
  children,
  user,
}): JSX.Element => {
  switch (auth.type) {
    case PageAuthType.Auth0: {
      if (auth.statsig.clientInitializedData) {
        return (
          <BootstrappedFeatureFlagProvider
            auth={auth}
            clientInitializedData={auth.statsig.clientInitializedData}
          >
            {children}
          </BootstrappedFeatureFlagProvider>
        );
      }
      return (
        <AsyncFeatureFlagProvider env={auth.env} user={user}>
          {children}
        </AsyncFeatureFlagProvider>
      );
    }
    case PageAuthType.Lti:
      return (
        <AsyncFeatureFlagProvider env={auth.env} user={user}>
          {children}
        </AsyncFeatureFlagProvider>
      );
  }
};

/**
 * Wraps the Statsig SDK's {@link StatsigProvider} to include information about the authenticated
 * user in the Statsig user object, {@link StatsigUser}.
 *
 * When a server-produced bootstrap payload is present on the auth prop (the
 * {@link StatsigPageAuth.clientInitializedData}), the client SDK is initialized synchronously from
 * that payload; otherwise it falls back to an asynchronous client-side initialization.
 *
 * @see https://docs.statsig.com/client/reactSDK
 */
export const FeatureFlagProvider: FC<FeatureFlagProviderProps> = props => {
  if (process.env.NODE_ENV === 'test') {
    util.logUsageContext();
    return props.children;
  }
  return <FeatureFlagProviderInner {...props} />;
};
